import { trpc } from "./trpc";

// Error deduplication cache - prevent spam logging of same errors
const errorCache = new Map<string, number>();
const CACHE_TTL_MS = 30000; // 30 seconds

// Offline error queue - up to 10 errors queued when offline
let offlineErrors: Array<{
  level: "error" | "warn";
  message: string;
  details?: any;
  stack?: string;
}> = [];
const MAX_OFFLINE_QUEUE = 10;

// Flag to prevent recursion when reporting client errors
let reportingError = false;

function generateErrorKey(message: string, path?: string): string {
  const key = `${message}:${path || window.location.pathname}`;
  return btoa(key).slice(0, 32); // Short hashed key
}

function shouldSkipError(message: string, path?: string): boolean {
  const key = generateErrorKey(message, path);
  const now = Date.now();
  const lastSeen = errorCache.get(key);

  if (lastSeen && (now - lastSeen) < CACHE_TTL_MS) {
    return true; // Skip recently logged error
  }

  errorCache.set(key, now);

  // Clean old entries (convert to array to avoid iterator issues)
  Array.from(errorCache.entries()).forEach(([k, timestamp]) => {
    if (now - timestamp > CACHE_TTL_MS) {
      errorCache.delete(k);
    }
  });

  return false;
}

function queueOfflineError(errorData: typeof offlineErrors[0]) {
  if (offlineErrors.length >= MAX_OFFLINE_QUEUE) {
    offlineErrors.shift(); // Remove oldest
  }
  offlineErrors.push(errorData);

  console.warn("[ErrorReporting] Error queued for later (offline):", errorData.message);
}

function flushOfflineErrors() {
  if (!navigator.onLine || reportingError) return;

  const errorsToFlush = offlineErrors.splice(0);
  if (errorsToFlush.length === 0) return;

  console.info(`[ErrorReporting] Flushing ${errorsToFlush.length} queued offline errors`);

  errorsToFlush.forEach(errorData => {
    reportErrorToServer(errorData.level, errorData.message, errorData.details, errorData.stack);
  });
}

async function reportErrorToServer(
  level: "error" | "warn",
  message: string,
  details?: any,
  stack?: string
) {
  if (reportingError) return; // Prevent recursion

  try {
    reportingError = true;

    // Direct fetch call to avoid React hooks
    const response = await fetch('/api/trpc/system.reportClientError', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({
        json: {
          level,
          message: message.slice(0, 500), // Cap message length
          source: "frontend",
          route: window.location.pathname,
          userAgent: navigator.userAgent,
          details,
          stack: stack ? stack.slice(0, 2000) : null, // Cap stack length
        }
      }),
    });

    if (!response.ok) {
      // Don't throw - we don't want to report errors about error reporting
      console.warn("[ErrorReporting] Failed to report error, server returned:", response.status);
      return;
    }

    console.debug(`[ErrorReporting] ${level} reported:`, message.slice(0, 100));
  } catch (reportingError) {
    // Don't report errors about reporting errors to avoid recursion
    if (reportingError instanceof Error && reportingError.message.includes('reportClientError')) {
      return;
    }
    console.warn("[ErrorReporting] Failed to report error:", reportingError);
  } finally {
    reportingError = false;
  }
}

function reportError(
  level: "error" | "warn",
  message: string,
  source?: string,
  error?: Error | any,
  additionalDetails?: any
) {
  if (shouldSkipError(message, source)) return;

  const details = {
    ...additionalDetails,
    timestamp: new Date().toISOString(),
  };

  if (error) {
    if (error instanceof Error) {
      details.name = error.name;
      details.stack = error.stack;
    }
    details.error = error;
  }

  if (navigator.onLine && !reportingError) {
    reportErrorToServer(level, message, details);
  } else {
    queueOfflineError({ level, message, details, stack: error?.stack });
  }
}



// Report global JavaScript errors and unhandled promises
function initGlobalErrorReporting() {
  // Global JavaScript errors
  window.addEventListener('error', (event) => {
    reportError(
      "error",
      `Global Error: ${event.message}`,
      event.filename || window.location.pathname,
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      },
      { globalError: true }
    );
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);

    reportError(
      "error",
      `Unhandled Promise Rejection: ${message}`,
      window.location.pathname,
      reason instanceof Error ? reason : { reason },
      { unhandledRejection: true, promiseReason: reason }
    );
  });

  // Online/offline status changes for flushing queued errors
  window.addEventListener('online', () => {
    console.info("[ErrorReporting] Back online, flushing queued errors");
    setTimeout(flushOfflineErrors, 1000); // Small delay to ensure network is stable
  });
}

export function initFrontendErrorReporting() {
  initGlobalErrorReporting();

  console.info("[ErrorReporting] Frontend error reporting initialized");
}

export function reportTestError() {
  // Deliberately trigger an error for testing
  setTimeout(() => {
    throw new Error("Test Frontend Error - This should be logged to system logs [TEST]");
  }, 10);
}

// Export for potential use in error boundaries
export { reportError };
